/**
 * Pure helper for the "Leave Household" guard logic.
 *
 * The function is DOM-free and Firestore-free — it inspects the household
 * shape and returns either an `Allowed` payload (the field deltas the caller
 * applies via `updateDoc`) or a `Blocked` payload describing why the user
 * cannot leave. Mirrors the iOS canonical guard flow in
 * `HouseholdsView.swift` (the leave action would otherwise orphan an active
 * household or strip the last admin).
 *
 * Two safety rules apply, matching the spec for Wave N.1a:
 *
 * 1. **Cannot leave the active household.** Users must switch to another
 *    household first. This avoids landing them in a "no-active-household"
 *    state that crashes downstream subscriptions.
 * 2. **Cannot leave if you are the last admin.** `household_owner` is the
 *    sole admin field today, so removing the only owner orphans the
 *    household. Front-door rule: at least one owner must remain.
 *
 * Acceptable shape returned by `prepareLeaveHousehold`:
 *
 * - `kind: "blocked"` with a stable `reason` enum the UI can map to a
 *   localized string. The reason set is closed (TypeScript discriminated
 *   union).
 * - `kind: "allowed"` with the field deltas to apply on both
 *   `households/{id}` and `member_profiles/{userId}`. Callers pass these
 *   straight to `updateDoc(...)`.
 *
 * Note on `arrayRemove`: this helper returns a marker object the test suite
 * can inspect without importing Firestore. The runtime caller substitutes
 * `arrayRemove(...)` from `firebase/firestore` when applying.
 */
import type { Household } from "./models";

export type LeaveHouseholdBlockedReason =
  | "active-household"
  | "last-admin"
  | "not-a-member";

export interface LeaveHouseholdAllowed {
  kind: "allowed";
  /**
   * Field deltas for `households/{householdId}`. Callers should map
   * `removeMember` to `arrayRemove(userId)` when applying via Firestore.
   */
  householdUpdates: {
    removeMember: string;
  };
  /**
   * Field deltas for `member_profiles/{userId}`. Callers should map
   * `removeHousehold` to `arrayRemove(householdId)`.
   */
  profileUpdates: {
    removeHousehold: string;
  };
}

export interface LeaveHouseholdBlocked {
  kind: "blocked";
  reason: LeaveHouseholdBlockedReason;
}

export type LeaveHouseholdResult =
  | LeaveHouseholdAllowed
  | LeaveHouseholdBlocked;

export interface LeaveHouseholdInput {
  /** The household the user is trying to leave. */
  household: Household;
  /** The household ID the user is trying to leave. */
  householdId: string;
  /** The user attempting to leave. */
  userId: string;
  /**
   * The currently-active household ID (the one in `HouseholdContext`).
   * If this matches `householdId`, leaving is blocked.
   */
  activeHouseholdId: string | null;
}

/**
 * Compute the result of attempting to leave a household.
 *
 * Returns a discriminated union so the UI can show the right error or
 * apply the right Firestore writes. Pure — no IO, no DOM access.
 */
export function prepareLeaveHousehold(
  input: LeaveHouseholdInput,
): LeaveHouseholdResult {
  const { household, householdId, userId, activeHouseholdId } = input;

  // Guard 1: the user must actually be a member of this household.
  const members = household.household_members ?? [];
  if (!members.includes(userId)) {
    return { kind: "blocked", reason: "not-a-member" };
  }

  // Guard 2: cannot leave the currently-active household — switch first.
  if (activeHouseholdId === householdId) {
    return { kind: "blocked", reason: "active-household" };
  }

  // Guard 3: cannot leave if you are the last admin (sole owner).
  // `household_owner` is the single-admin field in the current schema.
  // If the leaving user is the owner and there is no other member to
  // promote, the household would be orphaned.
  if (household.household_owner === userId) {
    const otherMembers = members.filter((m) => m !== userId);
    if (otherMembers.length === 0) {
      return { kind: "blocked", reason: "last-admin" };
    }
  }

  return {
    kind: "allowed",
    householdUpdates: { removeMember: userId },
    profileUpdates: { removeHousehold: householdId },
  };
}
